import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BarChart3, Download, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const csvEscape = (v) => {
  const s = (v == null ? "" : String(v));
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const ReportsTab = () => {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await axios.get(`${API}/admin/reports/attendance`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setRows(data || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const totalCrew = rows.reduce((sum, r) => sum + (r.crew_count || 0), 0);

  const downloadCsv = () => {
    const header = ["Event Date", "Event Name", "Location", "Crew Count", "Crew (First Last)"];
    const lines = [header.map(csvEscape).join(",")];
    rows.forEach((r) => {
      const names = (r.crew || []).map((c) => c.name).join("; ");
      lines.push([r.event_date, r.event_name, r.location || "", r.crew_count, names].map(csvEscape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const range = (start || "all") + "_to_" + (end || "all");
    a.download = `crew-attendance-report_${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <TabsContent value="reports" data-testid="reports-content">
      <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-crew-yellow" />
            <h2 className="text-2xl font-bold text-crew-yellow">REPORTS</h2>
          </div>
          <p className="text-zinc-400 text-sm mt-1">
            Crew attendance by event. {rows.length} events • {totalCrew} total crew assignments. Export as a spreadsheet to email.
          </p>
          <div className="flex flex-wrap items-end gap-3 mt-4">
            <div>
              <Label className="text-xs text-zinc-500 uppercase font-bold">From</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                className="bg-black border-zinc-700 text-white h-9 mt-1" data-testid="report-start-date" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase font-bold">To</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                className="bg-black border-zinc-700 text-white h-9 mt-1" data-testid="report-end-date" />
            </div>
            <Button onClick={fetchReport} variant="outline" className="border-zinc-700 text-zinc-300 h-9" data-testid="report-run">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Run
            </Button>
            <Button onClick={downloadCsv} disabled={rows.length === 0}
              className="bg-crew-yellow text-black font-bold hover:bg-white h-9" data-testid="report-download-csv">
              <Download className="mr-2 h-4 w-4" /> Download Spreadsheet (CSV)
            </Button>
          </div>
        </div>

        <div className="p-4">
          {rows.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="no-report-rows">
              {loading ? "Loading..." : "No events in this range."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-zinc-400 border-b border-zinc-800 bg-zinc-900">
                  <tr>
                    <th className="py-3 px-3">Event Date</th>
                    <th className="py-3 px-3">Event Name</th>
                    <th className="py-3 px-3">Crew (First &amp; Last)</th>
                    <th className="py-3 px-3 text-center"># Crew</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.event_id} className="border-b border-zinc-800/50 hover:bg-zinc-900 align-top" data-testid={`report-row-${r.event_id}`}>
                      <td className="py-3 px-3 font-mono text-zinc-200 whitespace-nowrap">{r.event_date}</td>
                      <td className="py-3 px-3 text-crew-yellow font-medium">{r.event_name}</td>
                      <td className="py-3 px-3 text-zinc-300">
                        {r.crew_count === 0 ? <span className="text-zinc-600">—</span> : (r.crew || []).map((c) => c.name).join(", ")}
                      </td>
                      <td className="py-3 px-3 text-center font-bold text-white">{r.crew_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </TabsContent>
  );
};
